import SharedNavbar from "@/components/SharedNavbar";
import { MapPin, Users, Truck, Calendar, Star, ArrowRight } from "lucide-react";

const LOGO_URL = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/ds_logo_transparent_1343324419e041449c280c0bfa45d1f3.png";
const FLEET_URL = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/IMG_8680_57652abd937740419da1710ca8449b28.jpg";

export default function UeberUns() {
  return (
    <div className="min-h-screen bg-background">
      <SharedNavbar />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={FLEET_URL} alt="DS Logistik Fuhrpark" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-24 lg:px-8">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/80 backdrop-blur-sm">
            <Calendar className="h-3.5 w-3.5 text-primary" />
            Seit 2005 · Schweizweit
          </div>
          <h1 className="text-4xl font-bold text-white sm:text-5xl lg:text-6xl">Über DS Logistik</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/75">Von St. Gallen in die ganze Schweiz — eine Erfolgsgeschichte in Logistik und Möbelmontage.</p>
        </div>
      </section>

      {/* Geschichte */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Unsere Geschichte</p>
              <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">Von St. Gallen zur schweizweiten Präsenz</h2>
              <div className="mt-8 space-y-8">
                {[
                  { year: "2005", title: "Gründung in St. Gallen", text: "DS Logistik wurde 2005 in St. Gallen gegründet — mit dem klaren Ziel, professionelle Möbellogistik und -montage für den Schweizer Retail-Markt anzubieten. Mit einem kleinen Team und grossem Engagement legten wir den Grundstein für das, was DS Logistik heute ist." },
                  { year: "2008", title: "Erste Partnerschaft mit Retail-Kunden", text: "Nach dem Vertrauen der ersten Grosshandelspartner begann DS Logistik schnell zu wachsen. Wir erweiterten unsere Flotte und unser Serviceangebot auf Küchenmontage und Showroom-Service." },
                  { year: "2012", title: "Expansion in der Deutschschweiz", text: "Mit der Eröffnung neuer Verteilungspunkte in der Deutschschweiz konnten wir die Lieferzeiten auf 48 Stunden reduzieren und neue Kantone erschliessen." },
                  { year: "2018", title: "Nationale Abdeckung", text: "DS Logistik ist nun in allen 26 Kantonen präsent — von Genf bis St. Gallen, von Basel bis Lugano. Mit 58+ Verteilungspunkten und 150 Fahrzeugen decken wir die ganze Schweiz ab." },
                  { year: "Heute", title: "350+ Mitarbeitende. 250'000 Kunden/Jahr.", text: "Heute ist DS Logistik einer der führenden Möbellogistik-Partner der Schweiz — mit 350+ Mitarbeitenden, 150 Fahrzeugen und über 250.000 zufriedenen Kunden pro Jahr. Unsere Werte bleiben dieselben wie 2005: Präzision, Zuverlässigkeit und Kundennähe." },
                ].map(({ year, title, text }) => (
                  <div key={year} className="flex gap-5">
                    <div className="flex flex-col items-center">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">{year === "Heute" ? "✓" : year.slice(-2)}</div>
                      <div className="mt-2 w-px flex-1 bg-border/60" />
                    </div>
                    <div className="pb-8">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs font-bold text-primary">{year}</span>
                      </div>
                      <h3 className="mt-1 text-lg font-bold text-foreground">{title}</h3>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-4">
              <div className="rounded-3xl border border-border/60 bg-card p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary mb-6">DS Logistik in Zahlen</p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { n: "2005", l: "Gegründet", icon: Calendar },
                    { n: "350+", l: "Mitarbeitende", icon: Users },
                    { n: "150", l: "Fahrzeuge 3,5t", icon: Truck },
                    { n: "58+", l: "Standorte CH", icon: MapPin },
                  ].map(({ n, l, icon: Icon }) => (
                    <div key={l} className="rounded-2xl bg-muted/40 p-4 text-center">
                      <Icon className="mx-auto mb-2 h-5 w-5 text-primary" />
                      <p className="font-mono text-2xl font-bold text-primary">{n}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{l}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-border/60 bg-card p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary mb-4">Unsere Werte</p>
                {[
                  { icon: Star, title: "Präzision", text: "Jede Lieferung, jede Montage — mit höchster Sorgfalt ausgeführt." },
                  { icon: Truck, title: "Zuverlässigkeit", text: "48h Lieferversprechen. Pünktlich. Immer." },
                  { icon: Users, title: "Kundennähe", text: "Persönlicher Service vor Ort — bei Ihnen zu Hause oder im Showroom." },
                ].map(({ icon: Icon, title, text }) => (
                  <div key={title} className="flex gap-3 py-3 border-b border-border/40 last:border-0">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="text-xs leading-5 text-muted-foreground">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Leadership</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">Das Führungsteam</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
            {[
              { name: "Driton Selmani", role: "CEO", desc: "Mitgründer und Visionär von DS Logistik. Verantwortlich für Strategie, Wachstum und Partnerbeziehungen seit der Gründung 2005 in St. Gallen." },
              { name: "Almedina Selmani", role: "CEO", desc: "Mitgründerin von DS Logistik. Verantwortlich für Operations, Qualitätsmanagement und die Entwicklung des nationalen Servicenetzwerks." },
              { name: "Sadik Alihajdaraj", role: "COO", desc: "Chief Operating Officer. Verantwortlich für die täglichen Logistikoperationen, Flottenmanagement und die Koordination der 350+ Mitarbeitenden." },
              { name: "André Stalder", role: "Geschäftsführer", desc: "Geschäftsführer mit Verantwortung für Finanzen, Compliance und institutionelle Partnerschaften in der Deutschschweiz." },
            ].map(({ name, role, desc }) => (
              <div key={name} className="rounded-3xl border border-border/60 bg-card p-6">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-xl font-bold text-primary">
                  {name.split(" ").map((n: string) => n[0]).join("")}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">{role}</p>
                <h3 className="mt-1 text-lg font-bold text-foreground">{name}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground py-16 text-white">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">Werden Sie unser Partner</h2>
          <p className="mt-4 text-white/65">Seit 2005 vertrauen Retail-Unternehmen auf DS Logistik. Wir freuen uns auf Ihre Anfrage.</p>
          <a href="/#/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-lg hover:-translate-y-px transition-transform">
            Kontakt aufnehmen <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <footer className="border-t border-border/40 bg-background py-6 text-center">
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} DS Logistik · Bielstrasse 20, 4500 Solothurn · <a href="/#/impressum" className="hover:text-primary">Impressum</a></p>
      </footer>
    </div>
  );
}
